const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const getBaseUrl = () => {
  if (!API_BASE_URL) {
    throw new Error('API base URL is not configured. Please set REACT_APP_API_BASE_URL.');
  }
  return API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
};

const handleResponse = async (response) => {
  const json = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message = json?.message || `HTTP ${response.status}`;
    throw new Error(message);
  }
  if (json && json.success === false) {
    throw new Error(json?.message || 'API responded with an error.');
  }
  return json;
};

export const bulkImportService = {
  /**
   * Download sample Excel file for a given module type
   * @param {string} type - Module type (e.g., 'customer', 'lead')
   * @returns {Promise<Blob>} - The Excel file blob
   */
  async downloadSample(type) {
    const base = getBaseUrl();
    const response = await fetch(`${base}/api/bulk-import/sample?type=${encodeURIComponent(type)}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const json = await response.json().catch(() => ({}));
      const message = json?.message || `HTTP ${response.status}`;
      throw new Error(message);
    }

    const blob = await response.blob();
    return blob;
  },

  /**
   * Validate Excel file before upload (legacy - file upload)
   * @param {string} type - Module type (e.g., 'customer', 'lead')
   * @param {File} file - The Excel file to validate
   * @returns {Promise<Object>} - Validation result with errors/warnings
   */
  async validateFile(type, file) {
    const base = getBaseUrl();
    const formData = new FormData();
    formData.append('type', type);
    formData.append('file', file);

    const response = await fetch(`${base}/api/bulk-import/validate`, {
      method: 'POST',
      body: formData,
    });

    const json = await handleResponse(response);
    return json?.data || json;
  },

  /**
   * Validate data array before upload
   * @param {string} type - Module type (e.g., 'customer', 'lead')
   * @param {Array} data - Array of objects to validate
   * @returns {Promise<Object>} - Validation result with errors/warnings
   */
  async validateData(type, data) {
    const base = getBaseUrl();
    
    const response = await fetch(`${base}/api/bulk-import/validate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        type,
        data,
      }),
    });

    const json = await response.json().catch(() => ({}));
    
    // Handle response format: { status: true, message: "...", data: {...} }
    if (!response.ok) {
      const message = json?.message || `HTTP ${response.status}`;
      throw new Error(message);
    }
    
    if (json.status === false) {
      throw new Error(json.message || 'Validation failed');
    }
    
    // Return the data object from response
    return json?.data || json;
  },

  /**
   * Upload validated data array
   * @param {string} type - Module type (e.g., 'customer', 'lead')
   * @param {Array} data - Array of objects to upload
   * @param {string} createdBy - User who is creating the records
   * @returns {Promise<Object>} - Upload result with success/error details
   */
  async uploadFile(type, data, createdBy) {
    const base = getBaseUrl();
    
    const response = await fetch(`${base}/api/bulk-import/upload`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        type,
        data,
        createdBy,
      }),
    });

    const json = await response.json().catch(() => ({}));
    
    // Handle response format: { status: true/false, message: "...", data: {...} }
    if (!response.ok || json.status === false) {
      const message = json?.message || `HTTP ${response.status}`;
      throw new Error(message);
    }
    
    // Return the full response object (with status, message, data)
    return json;
  },
};

